import React, { useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Tooltip, DatePicker } from 'antd';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { ADMIN_DAILY_TRANSACTION_REPORT, GET_MY_LEDGER_REPORTS } from './../../../Utils/appConstants';
import { getRequest } from './../../../Utils/AppApi';
import { INRFormat, getUser } from './../../../Utils/Common';
import { getUTCToLocalDateTimeFormat, getExportFormat, queryDate } from './../../../Utils/function';

const user = getUser();

function ViewTransaction() {
    const history = useHistory();
    const csvLink = useRef();

    const [transactionId, setTransactionId] = useState('');
    const [fromDate, setFromDate] = useState(moment()); // Today
    const [toDate, setToDate] = useState(moment());     // Today
    const [isLoading, setLoading] = useState(false);
    const [ledgerData, setLedgerData] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [csvData, setCsvData] = useState([]);

    const dateFormat = 'DD-MM-YYYY';

    // ─── Ledger Columns ───
    const ledgerColumns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Trans ID',
            selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
            width: "180px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Ref ID / Name',
            selector: row => row.toUserData?.username + " / " + row.toUserData?.name || '-',
            width: "250px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Service Name',
            selector: row => <Tooltip placement="bottom" title={row.serviceName}>{row.serviceName}</Tooltip>,
            width: "200px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Tran Amt',
            selector: row => row.transactionAmt ? INRFormat(row.transactionAmt) : "-",
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Opening Balance',
            selector: row => row.openingBalance ? INRFormat(row.openingBalance) : "-",
            width: "150px",
        },
        {
            name: 'Debit',
            selector: row => (!row.debit) ? "-" : INRFormat(row.debit),
            width: "130px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Credit',
            selector: row => (!row.credit) ? "-" : INRFormat(row.credit),
            width: "130px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Ser. Charge',
            selector: row => row.serviceCharge ? INRFormat(row.serviceCharge) : "-",
        },
        {
            name: 'Commission',
            selector: row => row.commission ? INRFormat(row.commission) : "-",
        },
        {
            name: 'TDS',
            selector: row => row.tds ? INRFormat(row.tds) : "-",
        },
        {
            name: 'Closing Balance',
            selector: row => INRFormat(row.closingBalance),
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Description',
            selector: row => row.description ? <Tooltip placement="bottom" title={row.description}>{row.description}</Tooltip> : "-",
            width: "250px",
        },
    ];

    // ─── Daily Transaction Report Columns ───
    const reportColumns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Trans ID',
            selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
            width: "180px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Ref ID / Name',
            selector: row => row.users?.username + " / " + row.users?.name || '-',
            width: "250px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Pancard',
            selector: row => row.users?.pancard || '-',
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'State',
            selector: row => (row.shopsState?.length > 0) ? row.shopsState[0] : row.stateName || '-',
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Service Name',
            selector: row => <Tooltip placement="bottom" title={row.serviceName}>{row.serviceName}</Tooltip>,
            width: "200px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Tran Amt',
            selector: row => row.transactionAmt ? INRFormat(row.transactionAmt) : "-",
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Tran type',
            selector: row => row.debit ? "DR" : "CR",
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Opening Balance',
            selector: row => row.openingBalance ? INRFormat(row.openingBalance) : "-",
            width: "150px",
        },
        {
            name: 'Debit',
            selector: row => (!row.debit) ? "-" : INRFormat(row.debit),
            width: "130px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Credit',
            selector: row => (!row.credit) ? "-" : INRFormat(row.credit),
            width: "130px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Ser. Charge',
            selector: row => row.serviceCharge ? INRFormat(row.serviceCharge) : "-",
        },
        {
            name: 'Commission',
            selector: row => row.commission ? INRFormat(row.commission) : "-",
        },
        {
            name: 'TDS',
            selector: row => row.tds ? INRFormat(row.tds) : "-",
        },
        {
            name: 'TDS Applicable',
            selector: row => row.tds ? "YES" : "NO",
        },
        {
            name: 'Closing Balance',
            selector: row => INRFormat(row.closingBalance),
            width: "150px",
            headerStyle: () => ({ textAlign: "center" }),
        },
        {
            name: 'Description',
            selector: row => row.description ? <Tooltip placement="bottom" title={row.description}>{row.description}</Tooltip> : "-",
            width: "250px",
        },
    ];

    // ─── Normalise a single item (for CSV export) ───
    const normalizeItem = (item, type) => {
        if (type === 'ledger') {
            return {
                _id: item._id,
                transactionId: item.transactionId,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                toUserData: item.toUserData || {},
                serviceName: item.serviceName,
                transactionAmt: item.transactionAmt,
                debit: item.debit || 0,
                credit: item.credit || 0,
                openingBalance: item.openingBalance || 0,
                closingBalance: item.closingBalance || 0,
                serviceCharge: item.serviceCharge || 0,
                commission: item.commission || 0,
                tds: item.tds || 0,
                description: item.description || '',
                users: {},
                shopsState: [],
                stateName: '',
            };
        } else {
            return {
                _id: item._id,
                transactionId: item.transactionId,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                users: item.users || {},
                shopsState: item.shopsState || [],
                stateName: item.stateName || '',
                serviceName: item.serviceName,
                transactionAmt: item.transactionAmt,
                debit: item.debit || 0,
                credit: item.credit || 0,
                openingBalance: item.openingBalance || 0,
                closingBalance: item.closingBalance || 0,
                serviceCharge: item.serviceCharge || 0,
                commission: item.commission || 0,
                tds: item.tds || 0,
                description: item.description || '',
                toUserData: {},
            };
        }
    };

    // ─── Fetch data ───
    const fetchTransactionData = async () => {
        // Transaction ID is mandatory
        if (!transactionId || !transactionId.trim()) {
            alert('Please enter a Transaction ID.');
            return;
        }

        setLoading(true);
        try {
            const encodedId = encodeURIComponent(transactionId.trim());
            const fromStr = queryDate(fromDate); // DD-MM-YYYY
            const toStr = queryDate(toDate);     // DD-MM-YYYY

            // Build query parameters with fromDate & toDate
            const ledgerQuery = `?page=1&limit=50&searchTransId=${encodedId}&startDate=${fromStr}&endDate=${toStr}`;
            const reportQuery = `?page=1&limit=50&searchTransactionId=${encodedId}&fromDate=${fromStr}&toDate=${toStr}`;

            const [ledgerRes, reportRes] = await Promise.all([
                getRequest(`${GET_MY_LEDGER_REPORTS}${ledgerQuery}`, history),
                getRequest(`${ADMIN_DAILY_TRANSACTION_REPORT}${reportQuery}`, history)
            ]);

            let ledgerItems = [];
            let reportItems = [];

            if (ledgerRes.success && ledgerRes.data) {
                const items = Array.isArray(ledgerRes.data)
                    ? ledgerRes.data
                    : (ledgerRes.data.docs || []);
                ledgerItems = items;
            }
            setLedgerData(ledgerItems);

            if (reportRes.success && reportRes.data) {
                const items = Array.isArray(reportRes.data)
                    ? reportRes.data
                    : (reportRes.data.docs || []);
                reportItems = items;
            }
            setReportData(reportItems);

            // ─── Prepare combined CSV ───
            const allNormalized = [
                ...ledgerItems.map(item => normalizeItem(item, 'ledger')),
                ...reportItems.map(item => normalizeItem(item, 'report'))
            ];
            const unique = allNormalized.filter((item, index, self) =>
                index === self.findIndex(t => t._id === item._id)
            );

            const csvRows = unique.map(e => ({
                "Created Date": getExportFormat(e.createdAt),
                "Updated Date": getExportFormat(e.updatedAt),
                "TXT Id": e.transactionId,
                "Ref ID": e.users?.username || e.toUserData?.username || '',
                "Pancard": e.users?.pancard || '',
                "State": (e.shopsState?.length > 0) ? e.shopsState[0] : e.stateName,
                "Name": e.users?.name || e.toUserData?.name || '',
                "Transaction Amt": e.transactionAmt,
                "Transaction Type": e.debit ? "DR" : "CR",
                "Service Name": e.serviceName,
                "Debit": e.debit,
                "Credit": e.credit,
                "Service Charge": e.serviceCharge,
                "Commission": e.commission,
                "TDS": e.tds,
                "TDS Applicable": e.tds ? "YES" : "NO",
                "Opening Balance": e.openingBalance,
                "Closing Balance": e.closingBalance,
                "Remark": e.description
            }));
            setCsvData(csvRows);

        } catch (error) {
            console.error("Error fetching transaction data:", error);
            alert('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ─── Handlers ───
    const handleSearch = () => fetchTransactionData();

    const handleClear = () => {
        setTransactionId('');
        setFromDate(moment());
        setToDate(moment());
        setLedgerData([]);
        setReportData([]);
        setCsvData([]);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    return (
        <div className="content-wrapper pt-2">
            <div className="content-header my-0 py-0">
                <div className="container-fluid">
                    <div className="row mb-0 align-items-center">
                        <div className="col-sm-6">
                            <h1 className='page-title text-blue font-merriweather'>
                                View Transaction <span className='text-red'>by ID & Date Range</span>
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid pt-2 pb-5">
                    <div className="card col-md-12">
                        <div className="card-body">
                            {/* Search Bar */}
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label className="text-blue">Transaction ID <span className="text-red">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control form-control-border"
                                            placeholder="e.g. SI26061016P00003"
                                            value={transactionId}
                                            onChange={(e) => setTransactionId(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label className="text-blue">From Date</label>
                                        <DatePicker
                                            className="form-control form-control-border"
                                            format={dateFormat}
                                            value={fromDate}
                                            onChange={(date) => setFromDate(date || moment())}
                                            placeholder="DD-MM-YYYY"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label className="text-blue">To Date</label>
                                        <DatePicker
                                            className="form-control form-control-border"
                                            format={dateFormat}
                                            value={toDate}
                                            onChange={(date) => setToDate(date || moment())}
                                            placeholder="DD-MM-YYYY"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3 d-flex align-items-end">
                                    <div className="btn-group" style={{ gap: '8px' }}>
                                        <button
                                            className="btn btn-primary rounded-pill px-4"
                                            onClick={handleSearch}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Searching...' : 'Search'}
                                        </button>
                                        <button
                                            className="btn btn-default rounded-pill px-4"
                                            onClick={handleClear}
                                        >
                                            Clear
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success rounded-pill px-4"
                                            onClick={() => csvLink.current?.link?.click()}
                                            disabled={csvData.length === 0}
                                        >
                                            <i className="fa fa-file-excel"></i> Export CSV
                                        </button>
                                        <CSVLink
                                            data={csvData}
                                            filename={`transaction-${transactionId}-${queryDate(fromDate)}_to_${queryDate(toDate)}`}
                                            className="hidden"
                                            ref={csvLink}
                                            target="_blank"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-12">
                                    <small className="text-muted">
                                        Transaction ID is mandatory. Dates are auto‑filled with today’s date.
                                    </small>
                                </div>
                            </div>

                            {/* ─── Ledger Section ─── */}
                            <div className="container-fluid pt-4">
                                <h3 className="text-blue">📊 Ledger Data</h3>
                                <DataTable
                                    columns={ledgerColumns}
                                    data={ledgerData}
                                    progressPending={isLoading}
                                    highlightOnHover
                                    pagination
                                    paginationRowsPerPageOptions={[10, 25, 50]}
                                    noDataComponent="No ledger records found for the given criteria"
                                />
                            </div>

                            {/* ─── Daily Transaction Report Section ─── */}
                            <div className="container-fluid pt-4">
                                <h3 className="text-blue">📋 Daily Transaction Report Data</h3>
                                <DataTable
                                    columns={reportColumns}
                                    data={reportData}
                                    progressPending={isLoading}
                                    highlightOnHover
                                    pagination
                                    paginationRowsPerPageOptions={[10, 25, 50]}
                                    noDataComponent="No report records found for the given criteria"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ViewTransaction;